import React, { useState, useEffect, useRef } from 'react';
import { Volume2, VolumeX, Play, Pause, ArrowRight } from 'lucide-react';

/**
 * SpiralOrg Cinema Experience
 * Premium theater experience with "Film of the Day"
 * Letterbox on mobile, full cinematic controls
 * Video uploaded by Super Architect from dashboard
 */
const SpiralOrgCinemaExperience = ({ onProceedToMain, onNavigateToAuth }) => {
  const videoRef = useRef(null);
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [filmData, setFilmData] = useState(null);
  const [isLandscape, setIsLandscape] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const hideControlsTimeout = useRef(null);

  useEffect(() => {
    // Fetch current "Film of the Day" from backend
    fetchFilmOfTheDay();
    
    // Detect orientation
    const checkOrientation = () => {
      setIsLandscape(window.innerWidth > window.innerHeight);
    };
    
    checkOrientation();
    window.addEventListener('resize', checkOrientation);
    window.addEventListener('orientationchange', checkOrientation);
    
    return () => {
      window.removeEventListener('resize', checkOrientation);
      window.removeEventListener('orientationchange', checkOrientation);
    };
  }, []);
  
  // Mouse movement handler for showing/hiding controls
  useEffect(() => {
    const handleMouseMove = () => {
      setShowControls(true);
      
      // Clear existing timeout
      if (hideControlsTimeout.current) {
        clearTimeout(hideControlsTimeout.current);
      }
      
      // Hide controls after 3 seconds of inactivity
      hideControlsTimeout.current = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('touchstart', handleMouseMove);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('touchstart', handleMouseMove);
      if (hideControlsTimeout.current) {
        clearTimeout(hideControlsTimeout.current);
      }
    };
  }, []);

  const fetchFilmOfTheDay = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cinema/film-of-the-day`);
      if (response.ok) {
        const data = await response.json();
        setFilmData(data);
      } else {
        // Default to new film
        setFilmData({
          url: 'https://customer-assets.emergentagent.com/job_spiralorg/artifacts/2p5jgv9v_Seedy%20Peeps%20Vid%201%20FINAL.mp4',
          title: 'Seedy Peeps - A Moment of Joy',
          description: 'Gleeful improvisational music healing'
        });
      }
    } catch (error) {
      console.error('Error fetching film:', error);
      // Default film
      setFilmData({
        url: 'https://customer-assets.emergentagent.com/job_spiralorg/artifacts/2p5jgv9v_Seedy%20Peeps%20Vid%201%20FINAL.mp4',
        title: 'Seedy Peeps - A Moment of Joy',
        description: 'Gleeful improvisational music healing'
      });
    }
  };

  useEffect(() => {
    if (videoRef.current && filmData) {
      const video = videoRef.current;
      video.muted = false; // Try unmuted first
      
      // Track video time
      const updateTime = () => {
        setCurrentTime(video.currentTime);
        setDuration(video.duration);
      };
      
      video.addEventListener('timeupdate', updateTime);
      video.addEventListener('loadedmetadata', updateTime);
      
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.then(() => {
          setIsPlaying(true);
          setIsMuted(false); // Audio playing
        }).catch(e => {
          console.log('Autoplay with audio blocked, trying muted:', e);
          // If audio autoplay blocked, try muted
          video.muted = true;
          setIsMuted(true);
          video.play().catch(e2 => console.log('All autoplay blocked:', e2));
          
          // Unmute on first interaction
          const unmute = () => {
            if (videoRef.current) {
              videoRef.current.muted = false;
              setIsMuted(false);
            }
          };
          
          document.addEventListener('click', unmute, { once: true });
        });
      }
      
      return () => {
        video.removeEventListener('timeupdate', updateTime);
        video.removeEventListener('loadedmetadata', updateTime);
      };
    }
  }, [filmData]);
  
  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    if (videoRef.current && duration) {
      videoRef.current.currentTime = pos * duration;
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(!isMuted);
    }
  };

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };
  
  const goFullscreen = () => {
    if (videoRef.current) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen();
      } else if (videoRef.current.webkitRequestFullscreen) {
        videoRef.current.webkitRequestFullscreen();
      } else if (videoRef.current.webkitEnterFullscreen) {
        // iOS Safari
        videoRef.current.webkitEnterFullscreen();
      }
    }
  };

  if (!filmData) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <p className="text-white text-xl">Loading cinema experience...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden relative">
      
      {/* CINEMA THEATER - Letterbox on ALL screens including mobile */}
      <div className="h-screen flex flex-col justify-center bg-black">
        
        {/* Top Letterbox Bar - Larger SpiralOrg branding */}
        <div className={`relative z-20 bg-black py-4 px-6 transition-opacity duration-500 ${showControls ? 'opacity-100' : 'opacity-0'}`}>
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            {/* Larger branding */}
            <p 
              className="text-lg md:text-xl tracking-widest text-white/60"
              style={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.35em' }}
            >
              SpiralOrg
            </p>
            
            {isMuted && (
              <span className="text-xs text-white/40 animate-pulse">Click for sound</span>
            )}
          </div>
        </div>

        {/* Video Container - Letterbox 2.39:1 on ALL screens */}
        <div 
          className="relative z-10 w-full bg-black"
          style={{ 
            aspectRatio: '2.39 / 1',
            maxHeight: '70vh'
          }}
        >
          <video 
            ref={videoRef}
            loop 
            playsInline
            webkit-playsinline="true"
            x5-playsinline="true"
            className="w-full h-full object-cover"
            style={{ 
              filter: 'brightness(0.85) contrast(1.1)',
              objectPosition: 'center center'
            }}
          >
            <source src={filmData.url} type="video/mp4" />
          </video>
          
          {/* Fullscreen Button - Now in video overlay */}
          <button
            onClick={goFullscreen}
            className={`absolute top-4 right-4 p-3 bg-black/60 backdrop-blur-sm border border-amber-500/40 hover:border-amber-500/70 rounded-lg transition-all z-20 ${showControls ? 'opacity-100' : 'opacity-0'}`}
            aria-label="Fullscreen"
          >
            <svg className="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
            </svg>
          </button>
          
          {/* Video Scrubber - Shows on hover */}
          <div 
            className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-0'}`}
          >
            <div 
              className="w-full h-1.5 bg-white/20 rounded-full cursor-pointer overflow-hidden hover:h-2 transition-all"
              onClick={handleSeek}
            >
              <div 
                className="h-full bg-amber-500 rounded-full transition-all"
                style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* Bottom Letterbox Bar - Film Info & Controls */}
        <div className={`relative z-20 bg-black py-8 px-6 transition-opacity duration-500 ${showControls ? 'opacity-100' : 'opacity-0'}`}>
          <div className="max-w-7xl mx-auto">
            {/* Cinema Explanation - No Film Title */}
            <div className="max-w-3xl mx-auto mb-6 px-4">
              <p 
                className="text-base md:text-lg leading-relaxed text-white/70 italic mb-3"
                style={{ fontFamily: "'Cormorant Garamond', serif", letterSpacing: '0.02em' }}
              >
                A rotating theatrical experience, where we attempt to give the vibe of what SpiralOrg is all about (sometimes comedic, dramatic, sometimes downright goofy, but always authentic, and mission driven...)
              </p>
              <p 
                className="text-base text-white/70 leading-relaxed mb-3"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                It's a planetary family of families, innovating, sharing, collaborating, celebrating and seeking to achieve hyper thrive. What more could we ask for?
              </p>
              <p 
                className="text-base text-amber-300/80"
                style={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.05em' }}
              >
                These artifacts are a portion of the lore, the lore of our current age. Apply for membership and be part of the guild.
              </p>
            </div>

            {/* Steampunk Controls with Fullscreen */}
            <div className="flex items-center justify-center gap-6">
              {/* Play/Pause */}
              <button
                onClick={togglePlayPause}
                className="p-3 bg-amber-900/30 hover:bg-amber-800/40 border border-amber-700/50 rounded-lg transition-all"
              >
                {isPlaying ? (
                  <Pause className="w-5 h-5 text-amber-500" />
                ) : (
                  <Play className="w-5 h-5 text-amber-500" />
                )}
              </button>

              {/* Mute/Unmute */}
              <button
                onClick={toggleMute}
                className="p-3 bg-amber-900/30 hover:bg-amber-800/40 border border-amber-700/50 rounded-lg transition-all"
              >
                {isMuted ? (
                  <VolumeX className="w-5 h-5 text-amber-500" />
                ) : (
                  <Volume2 className="w-5 h-5 text-amber-500" />
                )}
              </button>
              
              {/* Fullscreen - Moved from video overlay */}
              <button
                onClick={goFullscreen}
                className="p-3 bg-amber-900/30 hover:bg-amber-800/40 border border-amber-700/50 rounded-lg transition-all"
                aria-label="Fullscreen"
              >
                <svg className="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                </svg>
              </button>

              {/* Proceed to Main + Member Login */}
              <div className="flex items-center justify-center gap-4">
                <button
                  onClick={onProceedToMain}
                  className="px-8 py-3 bg-white/5 hover:bg-white/10 border border-white/20 hover:border-white/40 rounded-lg transition-all flex items-center gap-2"
                  style={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.1em' }}
                >
                  <span className="text-sm text-white/70">Tour SpiralOrg</span>
                  <ArrowRight className="w-4 h-4 text-white/70" />
                </button>
                
                <button
                  onClick={onNavigateToAuth || (() => window.location.hash = '#auth')}
                  className="px-8 py-3 bg-amber-600/80 hover:bg-amber-600 border border-amber-500/50 hover:border-amber-400 rounded-lg transition-all font-semibold"
                  style={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.1em' }}
                >
                  <span className="text-sm text-black">Member Login</span>
                </button>
              </div>
            </div>

            {/* Very Subtle Branding */}
            <p 
              className="text-xs text-white/30 mt-6 text-center tracking-widest"
              style={{ fontFamily: "'Cinzel', serif" }}
            >
              SPIRALORG CINEMA
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpiralOrgCinemaExperience;
